import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import { EventPinFocus } from "./eventIcons";

// Events pile up: an asset parked for half an hour reports from the same
// few metres over and over, and the map ends up with thirty pins stacked
// into what looks like one. Only the topmost is reachable, so clicking a
// stack has to offer what is underneath - which means knowing, at the
// current zoom, which pins are sitting on top of each other.


const STACK_RADIUS_PX = 18;

const TILE_SIZE = 256;

// Web Mercator world coordinates - the same thing
// google.maps.Projection.fromLatLngToPoint returns, computed directly so
// this needs no OverlayView and can run before the map has one.
const projectToWorld = (latitude: number, longitude: number) => {
  const siny = Math.min(
    Math.max(Math.sin((latitude * Math.PI) / 180), -0.9999),
    0.9999
  );
  return {
    x: TILE_SIZE * (0.5 + longitude / 360),
    y: TILE_SIZE * (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI)),
  };
};

// Which screen cell a point's pin falls in at this zoom. Two pins sharing a
// key are stacked.

export const screenCellKeyFor = (
  point: VehicleMapDataPoint,
  zoom: number
): string => {
  const scale = Math.pow(2, zoom);
  const world = projectToWorld(point.latitude, point.longitude);
  const cellX = Math.floor((world.x * scale) / STACK_RADIUS_PX);
  const cellY = Math.floor((world.y * scale) / STACK_RADIUS_PX);
  return `${cellX}:${cellY}`;
};

// --- consecutive repeats of one event

export type RunnableItem = {
  point: { statuscode: number; timestamp: number };
  // index within the trip, so a run is only a run if nothing happened
  // between its members
  pointIndex: number;
  tripKey?: string;
};

// Splits items into consecutive same-event runs, preserving order.
// Singletons come back as runs of one, so callers have a single shape.
//

export const groupIntoRuns = <T extends RunnableItem>(
  items: T[],
  canCollapse: (point: T["point"]) => boolean = () => true
): T[][] => {
  const runs: T[][] = [];
  items.forEach((item) => {
    const current = runs[runs.length - 1];
    const previous = current?.[current.length - 1];
    const continuesRun =
      previous !== undefined &&
      canCollapse(item.point) &&
      canCollapse(previous.point) &&
      previous.tripKey === item.tripKey &&
      previous.point.statuscode === item.point.statuscode &&
      item.pointIndex === previous.pointIndex + 1;
    if (continuesRun) current.push(item);
    else runs.push([item]);
  });
  return runs;
};

// The duration pill that rides beside a collapsed run's pin - "3m stopped"
// in Matt's words, minus the word, since the pin already carries the icon.
const RUN_LABEL_HEIGHT = 18;
const RUN_LABEL_FILL = "#1f2937";

// How far right of the position the pill starts, per pin size - the pill
// has to clear the pin's own half-width, and the pin grows when it is
// hovered or picked. A single clearance big enough for the largest left
// the pill marooned out in the road next to an ordinary pin.
const RUN_LABEL_OFFSET_BY_FOCUS: Record<EventPinFocus, number> = {
  none: 15,
  hover: 19,
  selected: 23,
};

export const runLabelIcon = (label: string, focus: EventPinFocus = "none") => {
  const width = Math.max(26, label.length * 7 + 12);
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${RUN_LABEL_HEIGHT}" viewBox="0 0 ${width} ${RUN_LABEL_HEIGHT}">
      <rect x="0.5" y="0.5" rx="${(RUN_LABEL_HEIGHT - 1) / 2}" ry="${(RUN_LABEL_HEIGHT - 1) / 2}" width="${width - 1}" height="${RUN_LABEL_HEIGHT - 1}" fill="${RUN_LABEL_FILL}" stroke="white" stroke-width="1.5" />
      <text x="${width / 2}" y="${RUN_LABEL_HEIGHT / 2 + 4}" font-family="Arial, sans-serif" font-size="10" font-weight="700" fill="white" text-anchor="middle">${label}</text>
    </svg>
  `;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(width, RUN_LABEL_HEIGHT),
    // Sits just right of the pin, level with its head. The pin's tip is
    // its anchor and its artwork runs 24px above that - see eventIcons.
    anchor: new google.maps.Point(
      -RUN_LABEL_OFFSET_BY_FOCUS[focus],
      28
    ),
  };
};
